import React from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text'
import Button from 'core-system/Button'
import Chip from 'core-system/Chip'
import palette from 'core-system/Themes/palette'

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1.25rem;
  border: 1px dashed ${(props) => props.theme.palette.grey.grey300};
  border-radius: 0.75rem;
  background-color: ${(props) => props.theme.palette.white};
`

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 0.75rem;
`

const IconContainer = styled.div<{ bgColor: string }>`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.5rem;
  background-color: ${(props) => props.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 0.75rem;
`

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const TypeLabel = styled.span`
  text-transform: uppercase;
  font-size: 0.625rem;
  font-weight: 500;
  letter-spacing: 0.5px;
  color: ${(props) => props.theme.palette.text.placeholder};
`

const ProgramName = styled(Text)`
  font-weight: 600;
  font-size: 1rem;
  margin-top: 0.25rem;
`

const ProgramDescription = styled(Text)`
  color: ${(props) => props.theme.palette.text.secondary};
  font-size: 0.875rem;
  line-height: 1.5;
  margin-bottom: 1.25rem;
`

const BottomSection = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: auto;
`

const BenefitText = styled.span`
  color: ${(props) => props.theme.palette.text.secondary};
  font-size: 0.8125rem;
  flex: 1;
`

// Transit icon
const TransitIcon = () => (
  <svg
    width='20'
    height='20'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <rect
      x='6'
      y='3'
      width='12'
      height='14'
      rx='2'
      stroke={palette.primary.pink600}
      strokeWidth='2'
    />
    <circle cx='9' cy='14' r='1.5' fill={palette.primary.pink600} />
    <circle cx='15' cy='14' r='1.5' fill={palette.primary.pink600} />
    <path
      d='M8 21L6 17H18L16 21'
      stroke={palette.primary.pink600}
      strokeWidth='2'
    />
    <line
      x1='6'
      y1='9'
      x2='18'
      y2='9'
      stroke={palette.primary.pink600}
      strokeWidth='2'
    />
  </svg>
)

// Bike icon for micromobility
const BikeIcon = () => (
  <svg
    width='20'
    height='20'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <circle cx='6' cy='16' r='3' stroke='#16a34a' strokeWidth='2' />
    <circle cx='18' cy='16' r='3' stroke='#16a34a' strokeWidth='2' />
    <path
      d='M6 16L9 9H15M15 9L18 16M15 9L12 13H9'
      stroke='#16a34a'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

// Parking icon
const ParkingIcon = () => (
  <svg
    width='20'
    height='20'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <rect
      x='4'
      y='4'
      width='16'
      height='16'
      rx='2'
      stroke={palette.accent.blue500}
      strokeWidth='2'
    />
    <path
      d='M9 16V8H13C14.6569 8 16 9.34315 16 11C16 12.6569 14.6569 14 13 14H9'
      stroke={palette.accent.blue500}
      strokeWidth='2'
      strokeLinecap='round'
    />
  </svg>
)

export type AvailableProgramType = 'transit' | 'parking' | 'micromobility'
export type TagVariant = 'employer' | 'pretax'

export interface AvailableProgramData {
  id: string
  type: AvailableProgramType
  name: string
  description: string
  tagVariant: TagVariant
  tagLabel: string
  benefitText: string
  logoUrl?: string
}

interface AvailableProgramCardProps {
  type: AvailableProgramType
  name: string
  description: string
  tagVariant: TagVariant
  tagLabel: string
  benefitText: string
  onClick: () => void
  isPending?: boolean
  logoUrl?: string
}

const getTypeConfig = (type: AvailableProgramType) => {
  switch (type) {
    case 'transit':
      return {
        label: 'TRANSIT PASS',
        bgColor: palette.primary.pink100,
        icon: <TransitIcon />,
      }
    case 'parking':
      return {
        label: 'PARKING',
        bgColor: palette.accent.blue100,
        icon: <ParkingIcon />,
      }
    case 'micromobility':
      return {
        label: 'MICROMOBILITY',
        bgColor: '#dcfce7',
        icon: <BikeIcon />,
      }
    default:
      return {
        label: 'PROGRAM',
        bgColor: palette.grey.grey100,
        icon: null,
      }
  }
}

const LogoImg = styled.img`
  height: 1.5rem;
  width: auto;
  max-width: 5rem;
  object-fit: contain;
`

const AvailableProgramCard: React.FC<AvailableProgramCardProps> = ({
  type,
  name,
  description,
  tagVariant,
  tagLabel,
  benefitText,
  onClick,
  isPending = false,
  logoUrl,
}) => {
  const typeConfig = getTypeConfig(type)
  const chipVariant = tagVariant === 'pretax' ? 'green' : 'blue'

  return (
    <CardContainer>
      <CardHeader>
        <IconContainer bgColor={typeConfig.bgColor}>
          {logoUrl ? <LogoImg src={logoUrl} alt={name} /> : typeConfig.icon}
        </IconContainer>
        <HeaderContent>
          <TopRow>
            <TypeLabel>{typeConfig.label}</TypeLabel>
            <Chip variant={isPending ? 'green' : 'grey'} width='auto'>
              {isPending ? 'PENDING' : 'AVAILABLE'}
            </Chip>
          </TopRow>
          <ProgramName variant='action3'>{name}</ProgramName>
        </HeaderContent>
      </CardHeader>
      <ProgramDescription variant='body2'>{description}</ProgramDescription>
      <BottomSection>
        <Chip variant={chipVariant} width='auto'>
          {tagLabel}
        </Chip>
        <BenefitText>{benefitText}</BenefitText>
        <Button
          variant={isPending ? 'tertiary' : 'primary'}
          size='small'
          onClick={onClick}
          disabled={isPending}
        >
          {isPending ? 'Requested' : 'Enroll'}
        </Button>
      </BottomSection>
    </CardContainer>
  )
}

export default React.memo(AvailableProgramCard)
